// TREASURY v2 — THE SCREEN'S MONEY LOGIC, COPIED FROM screens/TreasuryBalances.tsx (UI rebuild batch 2).
//
// The rebuild keeps the behaviour and changes the layout: every function here is the current screen's own, moved into a
// module so the v2 drawing can read it (the old screen does not export them, and it is not edited while it is main's
// drawing). Nothing is re-derived. The reasons for each rule — the wallet roll-up (4 Sept 2026), the outside reading
// (19 Sept), the crypto collapse, "est." once, the To-book line (21 Oct audit), the running account that is owed, not
// wrong — are written beside the originals in TreasuryBalances.tsx; read them there before changing anything here.
// When batch 2 ships and the old screen retires, this module is the one copy.
import { useState } from 'react';
import type {
  TreasuryGroup, TreasuryGroupLine as GroupLine, TreasuryGroupSource as GroupSource,
  TreasuryRailValue, TreasuryRailAsset, TreasuryValuation, StatedValuation, TreasuryOutside,
} from '../../../lib/api';
import { displayAsset, fmtAge, fmtAmount, collapseTotals } from '../../../lib/format';
import { isFiat } from '../../../lib/workflow';
import {
  railName, railSubtitle, isStale, partitionByRelevance, compareRelevance,
  foldAsset, sameAsset, valuationSourceName,
} from '../../../lib/naming';

export type { GroupLine, GroupSource };

// ── Folding, remembered (the same key as today's screen: what he folded stays folded across the switch) ─────────────
export const COLLAPSED_BY_DEFAULT = new Set(['acquired', 'other']);
const COLLAPSE_KEY = 'dubaiotc_treasury_collapsed';

export function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // Private mode, or a stale value from an older shape: the default, never a throw on a preference.
    }
    return new Set(COLLAPSED_BY_DEFAULT);
  });
  const persist = (next: Set<string>) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch { /* preference only */ }
  };
  return {
    isCollapsed: (id: string) => collapsed.has(id),
    toggle: (id: string) => {
      const next = new Set(collapsed);
      if (!next.delete(id)) next.add(id);
      persist(next);
    },
    setAll: (ids: string[], collapse: boolean) => persist(collapse ? new Set(ids) : new Set()),
    allCollapsed: (ids: string[]) => ids.length > 0 && ids.every((i) => collapsed.has(i)),
  };
}

// ── Lines and accounts ───────────────────────────────────────────────────────────────────────────────────────────
/** Per-asset lines whose display label collapses to one (USDTTRC20 + USDTTRC → one USDT·TRC line). */
export function foldAssetLines(lines: GroupLine[]): GroupLine[] {
  const out: GroupLine[] = [];
  const byLabel = new Map<string, GroupLine>();
  for (const l of lines) {
    const k = displayAsset(l.asset);
    const prev = byLabel.get(k);
    if (!prev) {
      const copy = { ...l, sources: [...(l.sources ?? [])] };
      byLabel.set(k, copy);
      out.push(copy);
    } else {
      prev.balance = (prev.balance ?? 0) + (l.balance ?? 0);
      prev.sources = [...(prev.sources ?? []), ...(l.sources ?? [])];
      if (!prev.rate && l.rate) { prev.rate = l.rate; prev.rate_quote = l.rate_quote; }
    }
  }
  return out;
}

/** The accounts behind a per-asset line, by relevance (the line's asset rides along for the dust floor). */
export function drillSources(line: GroupLine): Array<GroupSource & { asset: string | undefined }> {
  return (line.sources ?? []).map((s) => ({ ...s, asset: line.asset })).sort(compareRelevance);
}

/** Quantity × the rate a POSTING carried, display only. */
export function stampedValuation(line: GroupLine): { amount: number; quote?: string } | null {
  if (!line.rate || line.balance === undefined) return null;
  const r = Number(line.rate);
  if (!Number.isFinite(r) || r <= 0) return null;
  return { amount: line.balance * r, quote: line.rate_quote };
}
export function fmtStampedValuation(v: { amount: number; quote?: string }): string {
  return `≈ ${fmtAmount(v.amount, v.quote)}${v.quote ? ` ${v.quote}` : ''}`;
}

const WALLET_FIRST_GROUPS = new Set(['treasury_crypto']);
/** When a stated price stops looking fresh (six hours). */
const STALE_AFTER_HOURS = 6;

export type RailRow = TreasuryRailValue & { balances: TreasuryRailAsset[] };
export function railRow(rv: TreasuryRailValue): RailRow {
  return { ...rv, balances: rv.assets };
}

// ── The outside reading: the books hold 0, the chain or the venue shows money. Never priced, never in a subtotal. ──
interface OutsideLike { balance: number; outside?: TreasuryOutside | null }
export function isOutsideOnly(x: OutsideLike): boolean {
  return x.balance === 0 && !!x.outside;
}
export function waitingOutside(x: { balances?: readonly OutsideLike[] | null; sources?: readonly OutsideLike[] | null }): boolean {
  return [...(x.balances ?? []), ...(x.sources ?? [])].some(isOutsideOnly);
}
export interface Waiting { asset: string; outside: TreasuryOutside }
export function waitingCoins(rail: RailRow): Waiting[] {
  return rail.assets.filter(isOutsideOnly).map((a) => ({ asset: a.asset, outside: a.outside! }));
}
export function waitingOf(balances: ReadonlyArray<{ asset: string; balance: number; outside?: TreasuryOutside | null }>): Waiting[] {
  return balances.filter(isOutsideOnly).map((b) => ({ asset: b.asset, outside: b.outside! }));
}
/** "48,250 USDC" — one waiting coin, as the outside column and the To-book line both say it. */
export function waitingAmount(w: Waiting): string {
  return `${fmtAmount(w.outside.amount, w.asset)} ${displayAsset(w.asset)}`;
}

export interface ToBook { key: string; row: { slug?: string | null; label?: string | null }; waiting: Waiting[] }
/** Money waiting for a booking, from the rows that already carry the reading — mirrors what each group draws. */
export function toBook(groups: TreasuryGroup[], valuation?: TreasuryValuation): ToBook[] {
  const out: ToBook[] = [];
  for (const g of groups) {
    if (readsAsWallets(g, valuation)) {
      for (const rv of g.rails ?? []) {
        const waiting = waitingCoins(railRow(rv));
        if (waiting.length > 0) out.push({ key: rv.slug, row: rv, waiting });
      }
    } else if (g.style === 'asset_lines') {
      const byAccount = new Map<string, ToBook>();
      for (const l of foldAssetLines(g.lines)) {
        for (const s of l.sources ?? []) {
          if (!isOutsideOnly(s)) continue;
          const entry = byAccount.get(s.slug) ?? { key: s.slug, row: s, waiting: [] };
          entry.waiting.push({ asset: l.asset ?? '', outside: s.outside! });
          byAccount.set(s.slug, entry);
        }
      }
      out.push(...byAccount.values());
    } else {
      for (const l of g.lines) {
        const waiting = waitingOf(l.balances ?? []);
        if (waiting.length > 0) out.push({ key: l.key, row: l, waiting });
      }
    }
  }
  return out;
}
/** Money waiting is the opposite of quiet: such a row stays on screen whatever relevance said. */
function keepWaiting<T extends { balances?: readonly OutsideLike[] | null; sources?: readonly OutsideLike[] | null }>(
  p: { shown: T[]; hidden: T[] },
): { shown: T[]; hidden: T[] } {
  const waiting = p.hidden.filter(waitingOutside);
  if (waiting.length === 0) return p;
  return { shown: [...p.shown, ...waiting], hidden: p.hidden.filter((x) => !waitingOutside(x)) };
}
/** An account's state words — never "Dormant" / "Quiet" for one with money waiting outside. */
export function accountSubtitle<T extends Parameters<typeof railSubtitle>[0] & { balances?: readonly OutsideLike[] | null }>(
  row: T, opts?: Parameters<typeof railSubtitle>[1],
): string {
  return railSubtitle(waitingOutside(row) ? { ...row, balances: [] } : row, opts);
}

/** A group reads as wallets only when the BFF sent its valuation block. */
export function readsAsWallets(group: TreasuryGroup, valuation?: TreasuryValuation): boolean {
  return !!valuation && group.style === 'asset_lines' && WALLET_FIRST_GROUPS.has(group.id) && (group.rails?.length ?? 0) > 0;
}

export function splitRails(group: TreasuryGroup): { rails: RailRow[]; shown: RailRow[]; quiet: RailRow[] } {
  const rails = (group.rails ?? []).map(railRow);
  const { shown, hidden } = keepWaiting(partitionByRelevance(rails, { maxTier: 1 }));
  return { rails, ...walletOrder(rails, shown, hidden) };
}

/** THE WALLETS IN HIS ORDER (his Wallets ask, 30 Oct 2026, relayed by the Command Centre): customer Deposit wallets holding money, then Hot (by
 *  network, as the name sorts), then Treasury and Investment, then the rest (Merchant Hot, Fee, Flagged, others); a Deposit
 *  holding nothing goes behind the fold. The group is the name's first part — walletNames() writes Group · Network · Tag (N-091);
 *  the older labels, which start with the same group word, read the same. Within a group, the BFF's order stands. */
export function walletGroupRank(label: string | null): number {
  const l = (label ?? '').trim();
  if (/^Deposit\b/i.test(l)) return 0;
  if (/^Hot\b/i.test(l)) return 1;
  if (/^(Treasury|Investment)\b/i.test(l)) return 2;
  if (/^Merchant Hot\b/i.test(l)) return 3;
  if (/^Fee\b/i.test(l)) return 4;
  if (/^Flagged\b/i.test(l)) return 5;
  return 6;
}
const holdsMoney = (r: { assets: ReadonlyArray<{ balance: number }> }) => r.assets.some((a) => a.balance !== 0);
export function walletOrder<R extends { slug: string; label: string | null; assets: ReadonlyArray<{ balance: number }> }>(
  rails: readonly R[], shown: readonly R[], quiet: readonly R[],
): { shown: R[]; quiet: R[] } {
  const at = new Map(rails.map((r, i) => [r.slug, i]));
  const by = (a: R, b: R) => walletGroupRank(a.label) - walletGroupRank(b.label) || (at.get(a.slug) ?? 0) - (at.get(b.slug) ?? 0);
  const emptyDeposit = (r: R) => walletGroupRank(r.label) === 0 && !holdsMoney(r);
  return { shown: shown.filter((r) => !emptyDeposit(r)).sort(by), quiet: [...quiet, ...shown.filter(emptyDeposit)].sort(by) };
}

/** Quiet lines fold behind "+ N quiet …" and stay in every subtotal. */
export function splitLines(group: TreasuryGroup): { lines: GroupLine[]; shown: GroupLine[]; quiet: GroupLine[] } {
  const lines = group.style === 'asset_lines' ? foldAssetLines(group.lines) : group.lines;
  const { shown, hidden } = keepWaiting(partitionByRelevance(lines, { maxTier: 1 }));
  return { lines, shown, quiet: hidden };
}

/** Groups whose every line is stale sink to the bottom; none is ever dropped. */
export function orderGroups(groups: TreasuryGroup[]): TreasuryGroup[] {
  return groups
    .map((g, i) => ({ g, i, dead: g.lines.length > 0 && g.lines.every(isStale) }))
    .sort((a, b) => (a.dead === b.dead ? a.i - b.i : a.dead ? 1 : -1))
    .map((x) => x.g);
}

/** Where in a group a linked account sits: on show, behind the quiet fold, or not in it. */
export function whereIs(group: TreasuryGroup, valuation: TreasuryValuation | undefined, slug: string): 'shown' | 'quiet' | null {
  if (readsAsWallets(group, valuation)) {
    const r = splitRails(group);
    return r.shown.some((x) => x.slug === slug) ? 'shown' : r.quiet.some((x) => x.slug === slug) ? 'quiet' : null;
  }
  const l = splitLines(group);
  const has = (x: GroupLine) => x.slug === slug || (x.sources ?? []).some((s) => s.slug === slug);
  return l.shown.some(has) ? 'shown' : l.quiet.some(has) ? 'quiet' : null;
}

// ── What kind of number is this? (the wallet roll-up) ───────────────────────────────────────────────────────────
export type RollUpKind = 'unavailable' | 'unpriceable' | 'counted' | 'valued' | 'partial';
export interface RollUp {
  kind: RollUpKind;
  amount: number | null;
  quote: string;
  unpriced: string[];
  stated: { sources: string; as_of: string; age_hours: number | null } | null;
  stale: boolean;
}

export function rollUp(rv: TreasuryRailValue, v: TreasuryValuation): RollUp {
  const quote = v.quote;
  const stated = rv.stated_sources.length > 0 && rv.stated_as_of
    ? { sources: rv.stated_sources.map(valuationSourceName).join(' · '), as_of: rv.stated_as_of, age_hours: rv.stated_age_hours }
    : null;
  const stale = stated !== null && (stated.age_hours ?? 0) >= STALE_AFTER_HOURS;
  if (!v.available) return { kind: 'unavailable', amount: null, quote, unpriced: [], stated: null, stale: false };
  if (rv.value_usdt === null) {
    return rv.value_usdt_priced > 0
      ? { kind: 'partial', amount: rv.value_usdt_priced, quote, unpriced: rv.unpriced, stated, stale }
      : { kind: 'unpriceable', amount: null, quote, unpriced: rv.unpriced, stated, stale };
  }
  const counted = rv.assets.length > 0 && rv.assets.every((a) => foldAsset(a.asset) === foldAsset(quote));
  return { kind: counted ? 'counted' : 'valued', amount: rv.value_usdt, quote, unpriced: [], stated, stale };
}

/** One approximation mark per figure: never "≈ under 0.01". */
export function approx(text: string, mark = '≈'): string {
  return /^(≈|-?under )/.test(text) ? text : `${mark} ${text}`;
}

/** An estimate to the whole unit; a lower bound rounds DOWN. Display only. */
export function fmtEstimate(amount: number | null, quote: string, lowerBound = false): string {
  if (amount === null) return '—';
  if (lowerBound) {
    if (Math.abs(amount) >= 1) return fmtAmount(Math.floor(amount), quote, 0);
    return fmtAmount(Math.floor(amount * 100) / 100, quote);
  }
  return Math.abs(amount) >= 1 ? fmtAmount(amount, quote, 0) : fmtAmount(amount, quote);
}

/** A stated price's age, always in words (whole days past a week, where fmtAge goes silent). */
export function ageWords(iso: string, hours?: number | null): string {
  const age = fmtAge(iso);
  if (age) return age;
  const h = hours ?? (Date.now() - Date.parse(iso)) / 3_600_000;
  return Number.isFinite(h) ? `${Math.max(7, Math.round(h / 24))}d ago` : '';
}

// ── The crypto collapse: a totals map as one cumulative figure, under the same law as a wallet ─────────────────────
export interface AssetRate { rate: number; stated: StatedValuation | null }
export function assetRates(groups: TreasuryGroup[], valuation?: TreasuryValuation): Map<string, AssetRate> {
  const stated = new Map<string, StatedValuation>();
  for (const s of valuation?.stated ?? []) stated.set(foldAsset(s.asset), s);
  const rates = new Map<string, AssetRate>();
  for (const g of groups) {
    for (const rv of g.rails ?? []) {
      for (const a of rv.assets) {
        const fam = foldAsset(a.asset);
        if (rates.has(fam) || a.value_usdt === null || a.balance === 0) continue;
        rates.set(fam, { rate: a.value_usdt / a.balance, stated: a.basis === 'stated' ? stated.get(fam) ?? null : null });
      }
    }
  }
  return rates;
}

export function collapseAssets(amounts: Record<string, number>, rates: Map<string, AssetRate>, v: TreasuryValuation): RollUp {
  const quote = v.quote;
  if (!v.available) return { kind: 'unavailable', amount: null, quote, unpriced: [], stated: null, stale: false };
  const unpriced: string[] = [];
  const sources = new Set<string>();
  let priced = 0;
  let allQuote = true;
  let oldest: StatedValuation | null = null;
  for (const [asset, amount] of Object.entries(amounts)) {
    if (!sameAsset(asset, quote)) allQuote = false;
    const r = rates.get(foldAsset(asset));
    if (!r) { unpriced.push(asset); continue; }
    priced += amount * r.rate;
    if (r.stated) {
      sources.add(valuationSourceName(r.stated.source));
      if (oldest === null || r.stated.as_of < oldest.as_of) oldest = r.stated;
    }
  }
  const stated = oldest === null ? null : { sources: [...sources].sort().join(' · '), as_of: oldest.as_of, age_hours: oldest.age_hours };
  const stale = stated !== null && stated.age_hours >= STALE_AFTER_HOURS;
  if (unpriced.length > 0) {
    return priced === 0
      ? { kind: 'unpriceable', amount: null, quote, unpriced: unpriced.sort(), stated, stale }
      : { kind: 'partial', amount: priced, quote, unpriced: unpriced.sort(), stated, stale };
  }
  if (!allQuote && priced === 0 && unpriced.length === 0 && Object.keys(amounts).length === 0) {
    return { kind: 'unpriceable', amount: null, quote, unpriced: [], stated, stale };
  }
  return { kind: allQuote ? 'counted' : 'valued', amount: priced, quote, unpriced: [], stated, stale };
}

/** What a totals line LEADS with: the desk's currencies itemised, everything else one estimate. */
export interface Headline { fiat: Array<[string, number]>; crypto: RollUp | null; coins: number }
export function headline(totals: Record<string, number>, rates: Map<string, AssetRate>, v?: TreasuryValuation): Headline {
  const itemised = () => ({ fiat: Object.entries(collapseTotals(totals)).sort(), crypto: null, coins: 0 });
  if (!v || !v.available) return itemised();
  const fiat: Record<string, number> = {};
  const crypto: Record<string, number> = {};
  for (const [asset, amount] of Object.entries(totals)) (isFiat(asset) ? fiat : crypto)[asset] = amount;
  const coins = Object.keys(crypto).length;
  if (coins === 0) return itemised();
  return { fiat: Object.entries(collapseTotals(fiat)).sort(), crypto: collapseAssets(crypto, rates, v), coins };
}

/** THE STRIP (v2): the desk's currencies one figure each, and its coins as ONE figure — `—` when the rate book could
 *  not be read or the worker sent no valuation (a failed read is never a zero, and never a column per coin either: the
 *  exact quantities are in the groups below). */
export function stripTotals(totals: Record<string, number>, rates: Map<string, AssetRate>, v?: TreasuryValuation): Headline {
  const fiat: Record<string, number> = {};
  const crypto: Record<string, number> = {};
  for (const [asset, amount] of Object.entries(totals)) (isFiat(asset) ? fiat : crypto)[asset] = amount;
  const coins = Object.keys(crypto).length;
  const order = (a: [string, number], b: [string, number]) => (a[0] === 'AED' ? -1 : b[0] === 'AED' ? 1 : a[0].localeCompare(b[0]));
  const unread: RollUp = { kind: 'unavailable', amount: null, quote: v?.quote ?? 'USDT', unpriced: [], stated: null, stale: false };
  return {
    fiat: Object.entries(collapseTotals(fiat)).sort(order),
    crypto: coins === 0 ? null : v ? collapseAssets(crypto, rates, v) : unread,
    coins,
  };
}

export function coinsText(n: number): string { return n === 1 ? '1 coin' : `${n} coins`; }

/** Noun for a group's line count — wallets, currencies, assets, or accounts. */
export function lineNoun(groupId: string, n: number, wallets = false): string {
  const noun = wallets ? 'wallet' : groupId === 'office_cash' ? 'currency' : groupId === 'treasury_crypto' ? 'asset' : 'account';
  if (n === 1) return noun;
  return noun === 'currency' ? 'currencies' : `${noun}s`;
}
export function countText(groupId: string, n: number, wallets = false): string {
  return `${n} ${lineNoun(groupId, n, wallets)}`;
}

/** The oldest stated price feeding the desk's figures, and its sources — said once, in About this screen. */
export function statedStamp(valuation?: TreasuryValuation): { sources: string; as_of: string; age: string } | null {
  const s = valuation?.stated ?? [];
  if (s.length === 0) return null;
  const oldest = s.reduce((a, b) => (b.as_of < a.as_of ? b : a));
  const sources = [...new Set(s.map((x) => valuationSourceName(x.source)))].sort().join(' · ');
  return { sources, as_of: oldest.as_of, age: ageWords(oldest.as_of, oldest.age_hours) };
}

/** An account's name on the phone without its own desk in front ("DubaiOTC · Bybit" → "Bybit"). */
export function phoneRailName(line: { slug?: string | null; label?: string | null }, desk: string): string {
  const name = railName(line);
  const prefix = `${desk} · `;
  return name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

/** A group whose ONE row is open beneath its head does not say that row's figure twice. */
export function repeatsItsRow(group: TreasuryGroup, wallets: boolean, count: number, quietCount: number, open: boolean): boolean {
  if (!open || count !== 1 || quietCount > 0) return false;
  if (wallets || group.style === 'asset_lines') return true;
  return (group.lines[0]?.balances?.length ?? 0) === 1;
}

/** Groups whose heading already names their accounts' kind: a row there does not repeat it. */
export const KIND_IN_HEADING = new Set(['uae_banks', 'india_banks', 'india_cash', 'exchanges']);

/** A running account below zero is OWED, not wrong (the BFF's `running_account` flag). */
export function isOwed(line: GroupLine, balance: number): boolean {
  return line.running_account === true && balance < 0;
}
